/** biome-ignore-all lint/a11y/noStaticElementInteractions: <explanation> */
/** biome-ignore-all lint/a11y/useKeyWithClickEvents: <explanation> */
import { Close as CloseIcon, Menu as MenuIcon } from "@mui/icons-material";
import { BarChart3, Star, Trophy } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { useShinkaiIdentityQuery } from "../hooks/shinkai-identity";
import { getSmartContractUrl } from "../hooks/useSmartContractUrl";
import WalletButton from "./WalletButton";

function NavLink({
	href,
	to,
	onClick,
	children,
	external,
}: {
	href?: string;
	to?: string;
	onClick?: () => void;
	children: React.ReactNode;
	external?: boolean;
}) {
	const className =
		"px-3 py-2 text-sm text-text-default hover:text-text-secondary transition-colors";

	if (external && href) {
		return (
			<a
				href={href}
				target="_blank"
				rel="noreferrer"
				className={className}
				onClick={onClick}
			>
				{children}
			</a>
		);
	}

	return (
		<Link to={to || "/"} onClick={onClick} className={className}>
			{children}
		</Link>
	);
}

const ADMIN_NAV_ITEMS = [
	{
		label: "Manage Challenges",
		description: "Create and manage challenges",
		to: "/rewards",
		icon: Trophy,
	},
	{
		label: "Usage Monitoring",
		description: "View usage statistics and analytics",
		to: "/monitoring",
		icon: BarChart3,
	},
	{
		label: "Grant Points",
		description: "Grant points to users",
		to: "/rewards/grants",
		icon: Star,
	},
] as const;

function AdminQuickMenu() {
	return (
		<NavigationMenu delayDuration={120} skipDelayDuration={240}>
			<NavigationMenuList>
				<NavigationMenuItem>
					<NavigationMenuTrigger>Admin</NavigationMenuTrigger>
					<NavigationMenuContent className="min-w-[18rem] p-1.5 border border-divider bg-bg-default rounded-2xl shadow-lg">
						<ul className="grid gap-1">
							{ADMIN_NAV_ITEMS.map((item) => (
								<li key={item.to}>
									<NavigationMenuLink asChild>
										<Link
											to={item.to}
											className="flex select-none items-start gap-2.5 rounded-xl px-3 py-2.5 leading-none no-underline outline-none transition-colors hover:bg-bg-secondary focus:bg-bg-secondary"
										>
											<item.icon className="mt-0.5 h-4 w-4 text-text-tertiary shrink-0" />
											<span className="min-w-0">
												<span className="block text-sm text-text-default">
													{item.label}
												</span>
												<span className="block text-[11px] text-text-tertiary leading-tight">
													{item.description}
												</span>
											</span>
										</Link>
									</NavigationMenuLink>
								</li>
							))}
						</ul>
					</NavigationMenuContent>
				</NavigationMenuItem>
			</NavigationMenuList>
		</NavigationMenu>
	);
}

function PagesLinks({
	onClick,
	isAdmin,
	mobile = false,
}: {
	onClick?: () => void;
	isAdmin: boolean;
	mobile?: boolean;
}) {
	return (
		<>
			<NavLink to="/identities" onClick={onClick}>
				Identities
			</NavLink>
			<NavLink to="/quests" onClick={onClick}>
				Challenges
			</NavLink>
			<NavLink to="/leaderboard" onClick={onClick}>
				Leaderboard
			</NavLink>
			<NavLink href={getSmartContractUrl()} external onClick={onClick}>
				Smart Contract
			</NavLink>
			<NavLink href="https://faucet.shinkai.com" external onClick={onClick}>
				Faucet
			</NavLink>
			{isAdmin && !mobile && <AdminQuickMenu />}
			{isAdmin && mobile && (
				<>
					<NavLink to="/rewards" onClick={onClick}>
						Manage Challenges
					</NavLink>
					<NavLink to="/monitoring" onClick={onClick}>
						Usage Monitoring
					</NavLink>
					<NavLink to="/rewards/grants" onClick={onClick}>
						Grant Points
					</NavLink>
				</>
			)}
		</>
	);
}

export const ShinkaiLogo = ({ className }: { className?: string }) => {
	return (
		// biome-ignore lint/a11y/noSvgWithoutTitle: <ignore>
		<svg
			width="216"
			height="60"
			viewBox="0 0 216 60"
			fill="none"
			className={className}
			aria-label="Shinkai Logo"
		>
			<circle cx="30" cy="30" r="30" fill="#FE6162" />
			<path
				d="M45.9269 46.3536C45.65 45.3274 44.9038 44.8299 43.8628 44.9314C41.8474 45.1268 41.1782 44.4808 40.2218 43.0613C42.7423 43.5952 44.3679 42.9389 44.8756 42.6471C45.5295 42.2694 46.191 41.785 45.9295 40.9306C45.6731 40.0945 44.9038 39.9278 44.1141 40.0789C42.9936 40.2925 41.9192 40.4175 41.0474 39.3261C41.232 39.1594 41.4166 38.9875 41.6064 38.8234C42.0705 38.4249 42.4295 37.9561 42.1936 37.3231C41.9679 36.7188 41.3961 36.3724 40.8141 36.4792C40.3397 36.5678 39.3884 36.8647 39.0987 36.3802L39.1115 36.375C39.0013 36.349 38.832 35.8957 38.7961 35.5649C38.6269 33.9474 39.3269 32.395 40.2705 31.1057C41.4474 29.5038 42.2551 27.7378 42.6141 25.7791C43.1756 22.7055 42.5807 19.8456 40.9192 17.2174C38.9474 14.097 36.1679 12.0419 32.509 11.2865C31.65 11.1094 30.8115 11.0156 29.9987 11C29.1833 11.0156 28.3474 11.1094 27.4884 11.2865C23.832 12.0419 21.05 14.097 19.0782 17.2174C17.4192 19.8456 16.8243 22.7055 17.3833 25.7791C17.7397 27.7404 18.55 29.5038 19.7269 31.1057C20.6731 32.395 21.3731 33.9448 21.2013 35.5649C21.1654 35.8957 20.9961 36.349 20.8859 36.375L20.8987 36.3802C20.609 36.8621 19.6602 36.5678 19.1833 36.4792C18.6038 36.3724 18.0295 36.7188 17.8038 37.3231C17.5679 37.9561 17.9243 38.4249 18.391 38.8234C18.5807 38.9875 18.7654 39.1594 18.95 39.3261C18.0782 40.4175 17.0013 40.2925 15.8833 40.0789C15.0936 39.9278 14.3243 40.0945 14.0679 40.9306C13.8064 41.785 14.4679 42.2694 15.1218 42.6471C15.6269 42.9389 17.2551 43.5926 19.7756 43.0613C18.8192 44.4808 18.15 45.1268 16.1346 44.9314C15.0936 44.8299 14.3448 45.3274 14.0705 46.3536C13.8013 47.3564 14.332 48.0753 15.1731 48.5546C15.7243 48.8672 16.3397 48.9896 16.991 49C20.5577 49.0495 23.5013 47.5778 26.1525 45.3638C27.6115 44.1448 28.8115 43.5275 30.0013 43.5119C31.191 43.5275 32.3884 44.1448 33.85 45.3638C36.5013 47.5778 39.4448 49.0495 43.0115 49C43.6628 48.9922 44.2782 48.8672 44.8295 48.5546C45.6731 48.0753 46.2013 47.3564 45.932 46.3536H45.9269ZM25.0756 34.2027C23.9731 34.2001 23.0628 33.278 23.0602 32.1658C23.0602 31.0328 23.9807 30.1055 25.1166 30.0951C26.1961 30.0847 27.1577 31.1083 27.1474 32.257C27.1372 33.3952 26.2705 34.2079 25.0756 34.2027ZM34.9192 34.2027C33.7218 34.2079 32.8577 33.3926 32.8474 32.257C32.8372 31.1083 33.8013 30.0847 34.8782 30.0951C36.0141 30.1081 36.9346 31.0354 36.9346 32.1658C36.9346 33.278 36.0218 34.2001 34.9192 34.2027Z"
				fill="white"
			/>
			<path
				d="M212.321 21.0721C210.249 21.0721 208.599 19.3942 208.599 17.2859C208.599 15.1777 210.249 13.5428 212.321 13.5428C214.351 13.5428 216 15.1777 216 17.2859C216 19.3942 214.351 21.0721 212.321 21.0721ZM209.107 45.9404V24.4281H215.62V45.9404H209.107Z"
				fill="white"
			/>
			<path
				d="M191.621 46.5C187.138 46.5 184.347 43.8325 184.347 39.6161C184.347 35.6578 187.096 33.2054 192.171 32.8182L198.092 32.3449V32.0007C198.092 29.8925 196.823 28.7738 194.497 28.7738C191.748 28.7738 190.268 29.8494 190.268 31.7856H184.855C184.855 26.9238 188.788 23.7399 194.835 23.7399C200.967 23.7399 204.435 27.2249 204.435 33.3775V45.9407H198.684L198.261 43.1011C197.584 45.0802 194.793 46.5 191.621 46.5ZM193.905 41.5952C196.4 41.5952 198.134 40.3475 198.134 37.8951V36.7334L194.835 37.0346C192.002 37.2927 190.987 37.9381 190.987 39.3149C190.987 40.8638 191.917 41.5952 193.905 41.5952Z"
				fill="white"
			/>
			<path
				d="M168.373 45.9407H161.86V13.5H168.373V32.1728L175.224 24.4283H183.386L175.435 32.9472L183.132 45.9407H175.604L170.868 37.895L168.373 40.5626V45.9407Z"
				fill="white"
			/>
			<path
				d="M142.725 45.9407H136.213V24.4283H142.345L142.768 26.6656C144.079 24.8155 146.489 23.7399 149.238 23.7399C154.271 23.7399 157.189 27.0098 157.189 32.6461V45.9407H150.676V34.238C150.676 31.5274 149.196 29.7634 146.954 29.7634C144.375 29.7634 142.725 31.4844 142.725 34.1519V45.9407Z"
				fill="white"
			/>
			<path
				d="M128.028 21.0721C125.956 21.0721 124.306 19.3942 124.306 17.2859C124.306 15.1777 125.956 13.5428 128.028 13.5428C130.058 13.5428 131.707 15.1777 131.707 17.2859C131.707 19.3942 130.058 21.0721 128.028 21.0721ZM124.814 45.9404V24.4281H131.327V45.9404H124.814Z"
				fill="white"
			/>
			<path
				d="M105.68 45.9407H99.1668V13.5H105.722V26.6656C107.033 24.8155 109.444 23.7399 112.192 23.7399C117.225 23.7399 120.143 27.0098 120.143 32.646V45.9407H113.63V34.2379C113.63 31.5274 112.15 29.7634 109.909 29.7634C107.329 29.7634 105.68 31.4844 105.68 34.1519V45.9407Z"
				fill="white"
			/>
			<path
				d="M72.4652 23.3527C72.4652 17.5874 77.2441 13.5 83.9683 13.5C90.6926 13.5 94.9217 17.2862 94.9217 23.3096H88.1129C88.1129 21.0724 86.4635 19.6956 83.8838 19.6956C81.0926 19.6956 79.3163 20.9863 79.3163 23.0945C79.3163 25.0306 80.289 25.9772 82.4882 26.4505L87.1825 27.44C92.7649 28.6017 95.4292 31.3123 95.4292 36.3462C95.4292 42.4987 90.6926 46.5 83.5031 46.5C76.5251 46.5 72 42.6708 72 36.6904H78.8088C78.8088 39.0137 80.5005 40.3044 83.5454 40.3044C86.6327 40.3044 88.5781 39.0567 88.5781 37.0346C88.5781 35.2705 87.7745 34.367 85.7023 33.9368L80.9234 32.9472C75.341 31.7855 72.4652 28.5587 72.4652 23.3527Z"
				fill="white"
			/>
		</svg>
	);
};

export default function Navbar() {
	const [drawerOpened, setDrawerOpened] = useState(false);

	// Get admin status from query hook
	const { data: identityData } = useShinkaiIdentityQuery();
	const isAdmin = Boolean(identityData?.isAdmin);

	return (
		<>
			{/* Fixed Header */}
			<header className="fixed top-0 left-0 right-0 z-50 bg-bg-default py-2">
				<div className="max-w-7xl mx-auto px-0">
					<nav className="flex  w-full items-center justify-between gap-4 h-14  mx-auto   backdrop-blur-lg">
						{/* Logo */}
						<Link to="/" className="flex shrink-0">
							<ShinkaiLogo className="w-[85px] h-auto" />
						</Link>

						{/* Desktop Navigation */}
						<div className="hidden md:flex items-center justify-center grow gap-1">
							<PagesLinks isAdmin={isAdmin} />
						</div>

						{/* Desktop Actions */}
						<div className="hidden md:flex items-center gap-3">
							<WalletButton showChain />
						</div>

						{/* Mobile Actions */}
						<div className="flex md:hidden items-center gap-2">
							<WalletButton showChain />
							<button
								type="button"
								className="p-2 text-text-default hover:text-text-secondary transition-colors"
								aria-controls="mobile-menu"
								aria-haspopup="true"
								onClick={() => setDrawerOpened(true)}
							>
								<MenuIcon className="w-6 h-6" />
							</button>
						</div>
					</nav>
				</div>
			</header>

			{/* Mobile Menu Overlay */}
			{drawerOpened && (
				<div
					className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm md:hidden"
					onClick={() => setDrawerOpened(false)}
				/>
			)}

			{/* Mobile Menu Drawer */}
			<div
				id="mobile-menu"
				className={`fixed top-0 right-0 h-full w-full max-w-sm z-50 bg-bg-default transform transition-transform duration-300 ease-in-out md:hidden ${
					drawerOpened ? "translate-x-0" : "translate-x-full"
				}`}
			>
				<div className="flex flex-col h-full p-6">
					{/* Close Button */}
					<div className="flex justify-end">
						<button
							type="button"
							className="p-2 text-text-default hover:text-text-secondary transition-colors"
							onClick={() => setDrawerOpened(false)}
						>
							<CloseIcon className="w-6 h-6" />
						</button>
					</div>

					{/* Menu Content */}
					<div className="flex flex-col items-center justify-center grow gap-8">
						<ShinkaiLogo className="w-[120px] h-auto" />
						<div className="flex flex-col items-center gap-4">
							<PagesLinks
								isAdmin={isAdmin}
								mobile
								onClick={() => setDrawerOpened(false)}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
